/** Small display helpers shared by the vendor-quote flow screens. */

/** "YYYY-MM-DD" or ISO → "MM/DD/YYYY"; anything unparseable comes back as is. */
export function formatMdy(value: string): string {
  if (!value) return '';
  const d = new Date(value.length === 10 ? `${value}T12:00:00` : value);
  if (isNaN(d.getTime())) return value;
  return `${String(d.getMonth() + 1).padStart(2, '0')}/${String(d.getDate()).padStart(2, '0')}/${d.getFullYear()}`;
}

/** "N quote" / "N quotes" style plural. */
export function plural(n: number, word: string): string {
  return `${n} ${word}${n === 1 ? '' : 's'}`;
}

/** "2026-11-03 07:56:18" → "Nov 3, 2026 at 7:56 am"; anything unparseable comes back as is. */
export function formatSavedAt(value: string): string {
  if (!value) return '';
  const d = new Date(value.includes('T') ? value : value.replace(' ', 'T'));
  if (isNaN(d.getTime())) return value;
  const date = d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  const time = d.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' }).toLowerCase();
  return `${date} at ${time}`;
}

/** "799.5" → "$799.50"; blank for anything that isn't a number. */
export function formatUsd(value: any): string {
  const n = Number(value);
  if (!isFinite(n)) return '';
  return n.toLocaleString('en-US', { style: 'currency', currency: 'USD' });
}

/**
 * The "this quote is already here" card, built in one place so the Review screen and the Create
 * PO screen ask the same question the same way.
 *
 * `err` is the flattened 409 body from VendorQuoteDataService.saveDraft. The facts about the quote
 * already on file go in detail rows rather than the sentence — a buyer deciding between two copies
 * wants its amount and expiry, not a paragraph. The caller supplies the button text, because what
 * "cancel" does differs: Review goes back to the list, the PO screen prices the line from the
 * existing quote.
 */
export function duplicateQuoteDialogData(err: any, target: 'job' | 'bid', fallbackRef = ''): any {
  const existing = err?.existing || {};
  const ref = existing.quotation_reference || fallbackRef;
  const vendor = err?.vendor_name || '';
  const savedAt = formatSavedAt(existing.created_at);

  // Labels stay short on purpose: the popup's row style gives them a fixed 72px column.
  const rows: Array<{ label: string; value: string }> = [];
  if (ref) { rows.push({ label: 'Quote', value: ref }); }
  if (vendor) { rows.push({ label: 'Vendor', value: vendor }); }
  if (formatUsd(existing.amount)) { rows.push({ label: 'Amount', value: formatUsd(existing.amount) }); }
  if (existing.valid_till) { rows.push({ label: 'Valid till', value: formatMdy(existing.valid_till) }); }
  if (savedAt) { rows.push({ label: 'Saved', value: savedAt }); }

  return {
    title: 'This quote is already here',
    messageParts: [
      { text: 'Quote ' },
      { text: ref || 'with this number', bold: true },
      { text: vendor ? ' from ' : '' },
      { text: vendor, bold: true },
      { text: ` is already on this ${target}.` },
    ].filter(p => p.text !== ''),
    detailRows: rows,
    description: 'Two copies carry the same number, so nobody pricing a line later can tell them apart.',
    confirmIcon: 'content_copy',
    isWarning: true,
  };
}
